<script setup lang="ts">
/**
 * `clock` zone: a bare time overlay.
 *
 * Transparent background and shadowed white text, because the common placement
 * is a corner of a media zone. Text scales with the zone via container-query
 * height units (a vw clamp stands in where cqh is unsupported).
 *
 * No seconds, so the tick is minute-aligned: every screen in a venue flips the
 * minute on the same beat, and an idle wall does sixty times less work than a
 * per-second interval. A passed `now` freezes the clock for previews and tests,
 * the same convention as ProgrammeZone.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { ClockZoneParams } from './clock';

const props = defineProps<{
  params: ClockZoneParams;
  /** Fixed instant to render at. Omit for a live, minute-aligned clock. */
  now?: Date | undefined;
}>();

const emit = defineEmits<{
  warn: [message: string];
}>();

const tick = ref(new Date());
let timer: ReturnType<typeof setTimeout> | null = null;

function stopTicking(): void {
  if (timer) clearTimeout(timer);
  timer = null;
}

function scheduleTick(): void {
  // A chained timeout re-aligned on every fire, rather than an interval:
  // setInterval drifts, and a drifting clock flips its minute visibly late.
  const untilNextMinute = 60_000 - (Date.now() % 60_000);
  timer = setTimeout(() => {
    tick.value = new Date();
    scheduleTick();
  }, untilNextMinute + 50);
}

onMounted(() => {
  if (!props.now) scheduleTick();
});
onBeforeUnmount(stopTicking);

watch(
  () => props.now,
  (fixed) => {
    if (fixed) {
      stopTicking();
    } else if (!timer) {
      tick.value = new Date();
      scheduleTick();
    }
  },
);

const instant = computed(() => props.now ?? tick.value);

/**
 * The configured timezone if `Intl` accepts it, else the browser's. Only
 * `Intl` knows the IANA table, so this probe is the validation.
 */
const timezone = computed<string | undefined>(() => {
  const tz = props.params.timezone;
  if (!tz) return undefined;
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: tz });
    return tz;
  } catch {
    return undefined;
  }
});

watch(
  timezone,
  (tz) => {
    if (tz === undefined && props.params.timezone) {
      emit('warn', `clock-invalid-timezone:${props.params.timezone.slice(0, 80)}`);
    }
  },
  { immediate: true },
);

/** Time in the browser locale; the am/pm marker split out so it can be styled smaller. */
const time = computed<{ text: string; dayPeriod: string | null }>(() => {
  const options: Intl.DateTimeFormatOptions =
    props.params.format === '12h'
      ? { hour: 'numeric', minute: '2-digit', hour12: true }
      : { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' };
  if (timezone.value) options.timeZone = timezone.value;
  const parts = new Intl.DateTimeFormat(undefined, options).formatToParts(instant.value);
  const dayPeriod = parts.find((p) => p.type === 'dayPeriod')?.value ?? null;
  const text = parts
    .filter((p) => p.type !== 'dayPeriod')
    .map((p) => p.value)
    .join('')
    .trim();
  return { text, dayPeriod };
});

const dateLabel = computed<string | null>(() => {
  if (!props.params.showDate) return null;
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  };
  if (timezone.value) options.timeZone = timezone.value;
  return new Intl.DateTimeFormat(undefined, options).format(instant.value);
});
</script>

<template>
  <div class="lr42-clock" :class="{ 'lr42-clock--dated': dateLabel !== null }">
    <div class="lr42-clock__time">
      <span>{{ time.text }}</span>
      <span v-if="time.dayPeriod" class="lr42-clock__period">{{ time.dayPeriod }}</span>
    </div>
    <div v-if="dateLabel !== null" class="lr42-clock__date">{{ dateLabel }}</div>
  </div>
</template>

<style scoped>
.lr42-clock {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  container-type: size;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: var(--lr42-clock-color, #fff);
  font-family: var(--lr42-font, Georgia, 'Times New Roman', serif);
  /* Legible over whatever media the zone floats on. */
  text-shadow:
    0 2px 14px rgba(0, 0, 0, 0.55),
    0 0 3px rgba(0, 0, 0, 0.35);
  overflow: hidden;
}
.lr42-clock__time {
  line-height: 1;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  /* vw clamp first: an unsupported cqh invalidates only its own declaration. */
  font-size: clamp(24px, 9vw, 200px);
  font-size: 52cqh;
}
.lr42-clock--dated .lr42-clock__time {
  font-size: clamp(20px, 7vw, 160px);
  font-size: 40cqh;
}
.lr42-clock__period {
  font-size: 0.4em;
  margin-left: 0.18em;
  letter-spacing: 0.04em;
}
.lr42-clock__date {
  margin-top: 0.35em;
  font-size: clamp(11px, 2vw, 48px);
  font-size: 12cqh;
  color: var(--lr42-clock-date-color, rgba(255, 255, 255, 0.85));
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
</style>
